import { useEffect, useState } from "react";
import { Sliders } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";

export const AccessEditor = ({ target, onClose, onSaved }) => {
  const [meta, setMeta] = useState(null);
  const [packs, setPacks] = useState(target.access_packs || []);
  const [modules, setModules] = useState(target.access_modules || []);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/access/packs").then(({ data }) => setMeta(data)).catch((e) => toast.error(errMsg(e)));
  }, []);

  const toggle = (list, setList, key) =>
    setList(list.includes(key) ? list.filter((x) => x !== key) : [...list, key]);

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/admin/users/${target.user_id}/access`, { packs, modules });
      toast.success(`Access updated for ${target.email}`);
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4" data-testid="access-editor">
      <div className="bg-white rounded-md w-full max-w-2xl max-h-[88vh] overflow-y-auto">
        <div className="p-5 border-b border-[#E5E7EB] flex items-start gap-3">
          <Sliders className="h-5 w-5 text-[#002FA7] mt-0.5" />
          <div>
            <h2 className="font-heading font-bold text-[#111827]">Feature access — {target.name}</h2>
            <p className="text-xs text-[#6B7280] mt-0.5">{target.email} · choose a plan, and optionally tick extra modules.</p>
          </div>
        </div>

        <div className="p-5">
          <p className="text-xs uppercase tracking-[0.1em] text-[#6B7280] mb-2">Access plans</p>
          <div className="space-y-2">
            {(meta?.packs || []).map((p) => (
              <label key={p.key} className="flex items-start gap-3 rounded-md border border-[#E5E7EB] p-3 cursor-pointer hover:border-[#002FA7]">
                <Checkbox checked={packs.includes(p.key)} onCheckedChange={() => toggle(packs, setPacks, p.key)}
                  data-testid={`access-pack-${p.key}`} className="mt-0.5" />
                <span>
                  <span className="text-sm font-medium text-[#111827]">{p.label}</span>
                  <span className="block text-xs text-[#6B7280]">{p.desc}</span>
                </span>
              </label>
            ))}
          </div>

          <p className="text-xs uppercase tracking-[0.1em] text-[#6B7280] mt-6 mb-2">Extra individual modules</p>
          <div className="grid sm:grid-cols-2 gap-2">
            {(meta?.modules || []).map((m) => (
              <label key={m.key} className="flex items-center gap-2 text-sm cursor-pointer">
                <Checkbox checked={modules.includes(m.key)} onCheckedChange={() => toggle(modules, setModules, m.key)}
                  data-testid={`access-module-${m.key}`} />
                <span className="text-[#111827]">{m.label}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="p-4 border-t border-[#E5E7EB] flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} data-testid="access-cancel-btn">Cancel</Button>
          <Button onClick={save} disabled={busy} data-testid="access-save-btn" className="bg-[#002FA7] hover:bg-[#00237C] text-white">
            {busy ? "Saving…" : "Save access"}
          </Button>
        </div>
      </div>
    </div>
  );
};
